import { createContext, useContext, useEffect, useState } from "react";

const STORAGE_KEY = "opc_lang";

// Country / region definitions — drives currency, mobile money providers, locale.
export const COUNTRIES = {
    NG: {
        name: "Nigeria",
        flag: "🇳🇬",
        currency: "NGN",
        languages: ["en"],
        mobile_money: ["MTN MoMo", "Airtel Money", "9mobile", "OPay"],
        banks: ["GTBank", "Access Bank", "First Bank", "UBA", "Zenith"],
    },
    KE: {
        name: "Kenya",
        flag: "🇰🇪",
        currency: "KES",
        languages: ["en", "sw"],
        mobile_money: ["M-Pesa", "Airtel Money", "T-Kash"],
        banks: ["Equity", "KCB", "Co-operative", "NCBA", "ABSA"],
    },
    GH: {
        name: "Ghana",
        flag: "🇬🇭",
        currency: "GHS",
        languages: ["en"],
        mobile_money: ["MTN MoMo", "Vodafone Cash", "AirtelTigo Money"],
        banks: ["GCB", "Ecobank", "Absa", "Stanbic", "Zenith"],
    },
    ZA: {
        name: "South Africa",
        flag: "🇿🇦",
        currency: "ZAR",
        languages: ["en"],
        mobile_money: ["MTN MoMo", "Vodapay"],
        banks: ["FNB", "Standard Bank", "Nedbank", "Capitec", "ABSA"],
    },
    UG: {
        name: "Uganda",
        flag: "🇺🇬",
        currency: "UGX",
        languages: ["en", "sw"],
        mobile_money: ["MTN MoMo", "Airtel Money"],
        banks: ["Stanbic", "Centenary", "Equity", "Absa", "DFCU"],
    },
    TZ: {
        name: "Tanzania",
        flag: "🇹🇿",
        currency: "TZS",
        languages: ["en", "sw"],
        mobile_money: ["M-Pesa", "Airtel Money", "Tigo Pesa", "HaloPesa"],
        banks: ["CRDB", "NMB", "NBC", "Stanbic", "Equity"],
    },
};

// Language packs. Keys are flat dotted strings.
const STRINGS = {
    en: {
        // Common
        "common.welcome": "Welcome",
        "common.welcome_back": "Welcome back",
        "common.hello": "Hello",
        "common.signin": "Sign in",
        "common.signout": "Logout",
        "common.continue": "Continue",
        "common.cancel": "Cancel",
        "common.save": "Save",
        "common.confirm": "Confirm",
        "common.loading": "Loading…",
        "common.see_all": "See All",
        "common.view_all": "View All",
        "common.demo_accounts": "Demo accounts",
        "common.create_account": "Create an account",
        "common.processing": "Processing…",
        "common.email": "Email",
        "common.password": "Password",
        "common.full_name": "Full name",
        "common.phone": "Phone",
        "common.country": "Country",
        "common.currency": "Currency",
        "common.business_name": "Business name",
        "common.individual": "Individual",
        "common.business": "Business",
        "common.language": "Language",
        "common.notifications": "Notifications",
        "common.mark_all_read": "Mark all read",
        "common.no_notifications": "You're all caught up",
        "common.activity": "Activity",
        "common.profile": "Profile",
        "common.home": "Home",
        "common.credit": "Credit",
        "common.wallet": "Wallet",
        "common.loans": "Loans",
        "common.more": "More",
        "common.finance": "Finance",
        // Consumer home
        "home.good_to_see_you": "Good to see you again!",
        "home.credit_dna": "Credit DNA Score™",
        "home.your_on_track": "You're on track!",
        "home.view_factors": "View Score Factors",
        "home.onepass_wallet": "OnePass Wallet",
        "home.add_money": "Add Money",
        "home.send": "Send",
        "home.history": "History",
        "home.available_credit": "Available Credit",
        "home.unlock_larger_loans": "Unlock larger loans",
        "home.better_rates": "Better rates · up to ₦5M",
        // Loan apply
        "loan.how_much": "How much do you need?",
        "loan.select_tenure": "Select Tenure",
        "loan.repayment": "Repayment",
        "loan.due_in": "Due in {n} days",
        "loan.principal": "Principal",
        "loan.interest": "Interest",
        "loan.fees": "Fees",
        "loan.confirm_loan": "Confirm your loan",
        "loan.confirm_receive": "Confirm & Receive Funds",
        "loan.improve_score": "Improve your score",
        "loan.fast_disburse": "You'll receive funds in less than 60 seconds",
        "loan.approved": "Congratulations!",
        "loan.approved_subtitle": "Your loan is approved",
        "loan.disbursed_to_wallet": "Disbursed to your wallet",
        // Auth
        "auth.signin_continue": "Sign in to continue to OnePassCredit",
        "auth.create_account_t": "Create your account",
        "auth.join_in_minute": "Join OnePassCredit in under a minute",
        "auth.new_to_onepass": "New to OnePass?",
        "auth.have_account": "Already have an account?",
        // Wallet
        "wallet.balance": "Balance",
        "wallet.recent_txns": "Recent Transactions",
        "wallet.no_txns": "No transactions yet",
        "wallet.recipient": "Recipient",
        "wallet.amount": "Amount",
        "wallet.add_money_t": "Add Money",
        "wallet.send_money_t": "Send Money",
        "wallet.recipient_placeholder": "Phone or username",
        "wallet.invalid_amount": "Enter a valid amount",
        "wallet.added": "Added {amount}",
        "wallet.sent_to": "Sent {amount} to {to}",
        // Activity
        "activity.title": "Activity",
        "activity.subtitle": "Your money in motion",
        "activity.tab_all": "All",
        "activity.tab_credit": "Credit",
        "activity.tab_wallet": "Wallet",
        "activity.tab_payments": "Payments",
        "activity.today": "Today",
        "activity.yesterday": "Yesterday",
        "activity.empty": "No activity yet",
        "activity.success": "Success",
        // Loan apply (extra)
        "loan.step_of": "Step {n} of {total}",
        "loan.confirm_title": "Confirm your loan",
        "loan.product": "Product",
        "loan.amount": "Amount",
        "loan.tenure": "Tenure",
        "loan.total_repayment": "Total repayment",
        "loan.repay_on_time_title": "Repay on time",
        "loan.repay_on_time_sub": "Keep your payments on track",
        "loan.use_responsibly_title": "Use credit responsibly",
        "loan.use_responsibly_sub": "Borrow only what you need",
        "loan.grow_activity_title": "Grow your wallet activity",
        "loan.grow_activity_sub": "More activity, better offers",
        "loan.disbursed_to_wallet": "Disbursed to your wallet",
        "loan.repayment_details": "Repayment Details",
        "loan.due_date": "Due Date",
        "loan.approving": "Approving...",
        "loan.done": "Done",
        // LMS — schedule + statement + actions
        "loan.details": "Loan Details",
        "loan.outstanding": "Outstanding",
        "loan.repaid": "repaid",
        "loan.total": "total",
        "loan.late_fees": "Late fees",
        "loan.tenure_days": "{n}d tenure",
        "loan.disbursed_on": "Disbursed",
        "loan.due_on": "Due",
        "loan.schedule": "Schedule",
        "loan.statement": "Statement",
        "loan.repay_full": "Repay {amount}",
        "loan.early_settle": "Early settle & save",
        "loan.pay_installment": "Pay {amount}",
        "loan.installment_n_of": "Installment {n} of {total}",
        "loan.installment_n": "Installment {n}",
        "loan.installment_due": "Due {date}",
        "loan.status_paid": "Paid",
        "loan.status_upcoming": "Upcoming",
        "loan.status_partial": "Partial",
        "loan.status_overdue": "Overdue",
        "loan.no_events": "No events yet",
        "loan.early.title": "Early settlement",
        "loan.early.desc": "Pay off your loan now and keep 50% of remaining unaccrued interest.",
        "loan.early.accrued": "Accrued interest",
        "loan.early.unaccrued": "Unaccrued interest",
        "loan.early.discount": "Early-pay discount (50%)",
        "loan.early.settlement": "Settlement",
        "loan.early.confirm": "Settle now & save {amount}",
        "loan.early.processing": "Processing…",
        "loan.early.toast_saved": "Settled. You saved {amount}!",
        "loan.event.disbursed": "Disbursed to wallet",
        "loan.event.repaid": "Loan fully repaid",
        "loan.event.partial_repaid": "Partial repayment",
        "loan.event.auto_debited": "Auto-debit from wallet",
        "loan.event.late_fee": "Late fee applied",
        "loan.event.overdue": "Loan marked overdue",
        "loan.event.early_repaid": "Early settlement",
        "loan.event.restructured": "Restructured by admin",
        "loan.event.contacted": "Contacted by collections",

        // === Phase 4: Savings ===
        "savings.title": "OnePass Savings",
        "savings.kpi.total_saved": "Total saved",
        "savings.kpi.projected_interest": "Projected interest",
        "savings.kpi.buckets": "Buckets",
        "savings.tab.goals": "Goals",
        "savings.tab.deposits": "Deposits",
        "savings.tab.chamas": "Chamas",
        "savings.goal.new": "New savings goal",
        "savings.goal.empty": "Set a target for school fees, rent, or that big trip — and watch your balance grow.",
        "savings.goal.label": "Goal Savings",
        "savings.goal.add": "Add",
        "savings.goal.reached": "Reached",
        "savings.goal.withdraw_all": "Withdraw all to wallet",
        "savings.goal.auto_route": "Auto-routes {pct}% of every top-up",
        "savings.dep.new": "Lock a deposit & earn",
        "savings.dep.empty": "Lock idle wallet balance and earn up to 12% APR. Break early anytime for a small fee.",
        "savings.dep.label": "Fixed Deposits",
        "savings.dep.principal": "Principal",
        "savings.dep.interest": "Interest",
        "savings.dep.at_maturity": "At maturity",
        "savings.dep.locked_until": "Locked until {date}",
        "savings.dep.break_early": "Break early",
        "savings.chama.new": "Start a Chama",
        "savings.chama.label": "Chama Groups",
        "savings.chama.empty": "Rotating group savings — everyone contributes monthly, one member receives the pool each cycle.",
        "savings.chama.pool_now": "Pool now",
        "savings.chama.my_contribution": "My contribution",
        "savings.chama.target": "Target / cycle",
        "savings.chama.contribute": "Contribute {amount}",
        "savings.chama.join": "Join chama",
        "savings.chama.open_to_join": "Open Chamas to join",
        "savings.dlg.goal.title": "New savings goal",
        "savings.dlg.goal.desc": "Set a target, optionally route a % of each top-up automatically.",
        "savings.dlg.goal.template": "Template",
        "savings.dlg.goal.name": "Goal name",
        "savings.dlg.goal.target": "Target amount",
        "savings.dlg.goal.auto": "Auto-route % of every wallet top-up (0–100, optional)",
        "savings.dlg.goal.submit": "Create goal",
        "savings.dlg.dep.title": "Lock a fixed deposit",
        "savings.dlg.dep.desc": "Idle wallet funds earn tiered interest. Break early anytime for a small fee.",
        "savings.dlg.dep.term": "Choose term",
        "savings.dlg.dep.amount": "Amount to lock",
        "savings.dlg.dep.projected": "Projected interest",
        "savings.dlg.dep.payout": "Payout at maturity",
        "savings.dlg.dep.submit": "Lock deposit",
        "savings.dlg.chama.title": "Start a Chama",
        "savings.dlg.chama.desc": "Group rotating savings. Each cycle, everyone contributes; one member receives the full pool.",
        "savings.dlg.chama.name": "Chama name",
        "savings.dlg.chama.contribution": "Contribution / cycle",
        "savings.dlg.chama.freq": "Frequency",
        "savings.dlg.chama.weekly": "Weekly",
        "savings.dlg.chama.monthly": "Monthly",
        "savings.dlg.chama.desc_field": "Description (optional)",
        "savings.dlg.chama.submit": "Create chama",

        // === Phase 4: Insurance ===
        "insurance.title": "OnePass Insurance",
        "insurance.subtitle": "Protect your loans, devices, and health — covers attached straight to your wallet.",
        "insurance.tab.policies": "Policies",
        "insurance.tab.shop": "Shop",
        "insurance.tab.claims": "Claims",
        "insurance.empty.policies": "No active policies yet — tap Shop to buy cover.",
        "insurance.empty.claims": "No claims yet. Tap a policy → File a claim.",
        "insurance.get_cover": "Get cover",
        "insurance.file_claim": "File a claim",
        "insurance.cancel_policy": "Cancel",
        "insurance.policy.premium": "Premium",
        "insurance.policy.monthly": "Monthly",
        "insurance.policy.coverage": "Coverage",
        "insurance.policy.expires": "Expires",
        "insurance.claim.under_review": "Under review",
        "insurance.claim.approved": "Approved",
        "insurance.claim.paid": "Paid out",
        "insurance.claim.declined": "Declined",
        "insurance.product.loan_protection": "Loan Protection",
        "insurance.product.device_protection": "Device Protection",
        "insurance.product.health_micro": "Health Micro-Cover",
        "insurance.dlg.loan.title": "Loan Protection",
        "insurance.dlg.loan.desc": "0.8% one-time premium covers 100% of your loan principal in case of death or permanent disability.",
        "insurance.dlg.loan.active_loans": "Active loans",
        "insurance.dlg.loan.choose": "Choose loan to protect",
        "insurance.dlg.loan.submit": "Protect this loan",
        "insurance.dlg.device.title": "Device Protection",
        "insurance.dlg.device.desc": "Cover phones, laptops & tablets against theft, screen damage, water damage, and accidental drops.",
        "insurance.dlg.device.brand": "Brand",
        "insurance.dlg.device.model": "Model",
        "insurance.dlg.device.value": "Device value",
        "insurance.dlg.device.submit": "Protect device",
        "insurance.dlg.health.title": "Health Micro-Cover",
        "insurance.dlg.health.desc": "Outpatient consultations, lab tests, prescription drugs, hospital admission cap.",
        "insurance.dlg.health.beneficiaries": "Beneficiaries (comma-separated, optional)",
        "insurance.dlg.health.submit": "Activate cover",
        "insurance.dlg.claim.title": "File a claim",
        "insurance.dlg.claim.desc": "Tell us what happened and how much you're claiming. We review within 48 hours.",
        "insurance.dlg.claim.reason": "What happened?",
        "insurance.dlg.claim.amount": "Amount claimed",
        "insurance.dlg.claim.submit": "Submit claim",

        // === Phase 4: Investments ===
        "inv.title": "OnePass Investments",
        "inv.kpi.portfolio": "Portfolio",
        "inv.kpi.projected": "Projected",
        "inv.kpi.holdings": "Holdings",
        "inv.tab.tbills": "T-Bills",
        "inv.tab.bonds": "Bonds",
        "inv.tab.mmfs": "MMFs",
        "inv.tbill.buy": "Buy T-Bill",
        "inv.tbill.empty": "Buy government T-Bills at a discount and redeem at face value when they mature.",
        "inv.bond.buy": "Buy a Bond",
        "inv.bond.empty": "Government & infrastructure bonds with semi-annual coupon payments up to 16.5% p.a.",
        "inv.mmf.invest": "Invest in MMF",
        "inv.mmf.empty": "Money Market Funds — daily compounding, withdraw anytime in T+1.",
        "inv.col.paid": "Paid",
        "inv.col.discount": "Discount",
        "inv.col.at_maturity": "At maturity",
        "inv.col.principal": "Principal",
        "inv.col.total_coupons": "Total coupons",
        "inv.col.interest": "Interest",
        "inv.col.now": "Now",
        "inv.mmf.redeem": "Redeem",
        "inv.mmf.full": "Full",
        "inv.mmf.partial": "Partial",
        "inv.dlg.tbill.title": "Buy T-Bill",
        "inv.dlg.tbill.desc": "Treasury bills are short-term government instruments sold at a discount and redeemed at face value.",
        "inv.dlg.tbill.face": "Face value (received at maturity)",
        "inv.dlg.tbill.pay_today": "You pay today",
        "inv.dlg.tbill.discount_earned": "Discount earned",
        "inv.dlg.tbill.receive": "Receive at maturity",
        "inv.dlg.bond.title": "Buy a Bond",
        "inv.dlg.bond.desc": "Bonds pay semi-annual coupon interest and return your principal at maturity.",
        "inv.dlg.bond.amount": "Amount to invest",
        "inv.dlg.bond.coupons": "Coupons ({n}y total)",
        "inv.dlg.bond.final": "Final payout",
        "inv.dlg.mmf.title": "Invest in MMF",
        "inv.dlg.mmf.desc": "Money Market Funds — daily yield accrual, withdraw anytime in T+1.",
        "inv.dlg.mmf.amount": "Amount",
        "inv.dlg.mmf.submit": "Invest now",

        // === Phase 4: Commerce ===
        "shop.title": "OnePass Commerce",
        "shop.subtitle": "Shop from verified merchants · {merchants} stores · {suppliers} suppliers",
        "shop.tab.shop": "Shop",
        "shop.tab.merchants": "Stores",
        "shop.tab.suppliers": "Suppliers",
        "shop.tab.orders": "Orders",
        "shop.search": "Search products",
        "shop.empty.search": "No products found. Try a different search.",
        "shop.empty.merchants": "No active merchants yet.",
        "shop.empty.suppliers": "No suppliers in directory yet.",
        "shop.empty.orders": "No orders yet. Start shopping!",
        "shop.add": "Add",
        "shop.left": "left",
        "shop.checkout": "Checkout · {items} items · {total}",
        "shop.order.pending_fulfilment": "Awaiting fulfilment",
        "shop.order.fulfilled": "Fulfilled",
        "shop.order.cancelled": "Cancelled",
        "shop.order.cancel": "Cancel order",
        "shop.review.write": "Review {name}",
        "shop.review.title": "Review {name}",
        "shop.review.desc": "How was your experience? Your rating helps other shoppers.",
        "shop.review.comment": "Optional — share what you liked or didn't",
        "shop.review.submit": "Post review",
        "shop.merchant.products": "{n} products",

        // === Phase 4: KYC ===
        "kyc.title": "Identity Verification",
        "kyc.subtitle": "Verify your identity to unlock higher loan limits & a +80 credit score boost.",
        "kyc.status.title": "Status",
        "kyc.status.pending": "Not started",
        "kyc.status.submitted": "Under review",
        "kyc.status.verified": "Verified",
        "kyc.status.declined": "Declined",
        "kyc.reviewer_note": "Reviewer note:",
        "kyc.verified.title": "You're verified!",
        "kyc.verified.desc": "Higher loan limits & better rates unlocked. Credit score boosted +80.",
        "kyc.submitted.title": "Submitted",
        "kyc.submitted.desc": "Your documents are being reviewed. We'll notify you within 24 hours.",
        "kyc.form.id_type": "ID Type",
        "kyc.form.id_number": "ID Number",
        "kyc.form.id_placeholder": "e.g. 12345678901",
        "kyc.form.dob": "Date of birth",
        "kyc.form.address": "Address",
        "kyc.form.address_ph": "City, State",
        "kyc.upload.selfie": "Selfie",
        "kyc.upload.selfie_hint": "A clear photo of your face",
        "kyc.upload.id_front": "ID front",
        "kyc.upload.id_front_hint": "Front side of your ID document",
        "kyc.upload.id_back": "ID back (optional)",
        "kyc.upload.id_back_hint": "Back side, if applicable",
        "kyc.upload.replace": "Replace",
        "kyc.upload.upload": "Upload",
        "kyc.submit": "Submit for verification",
        "kyc.submitting": "Submitting…",

        // Profile KYC CTA
        "kyc.cta.verified": "Identity verified",
        "kyc.cta.submitted": "Under review",
        "kyc.cta.pending": "Verify your identity",
        "kyc.cta.verified_sub": "Higher limits & +80 score unlocked",
        "kyc.cta.submitted_sub": "Documents being reviewed — 24h SLA",
        "kyc.cta.pending_sub": "Unlock higher loan limits & +80 score boost",

        // Score Boosters
        "boosters.cta_title": "Score Boosters",
        "boosters.cta_sub": "See what lifts your Credit DNA Score",
        "boosters.title": "Score Boosters",
        "boosters.tier_now": "Current tier",
        "boosters.score_now": "Score now",
        "boosters.unlocked_count": "{n} of {total} unlocked",
        "boosters.next_tier": "Next: {tier}",
        "boosters.already_earned": "Already earned",
        "boosters.credit_points": "credit points",
        "boosters.up_for_grabs": "Up for grabs",
        "boosters.unlock_to_claim": "unlock to claim",
        "boosters.projected": "Unlock every booster to reach a projected score of {score}.",
        "boosters.status_unlocked": "Unlocked",

        // Home discovery tiles
        "tile.savings.label": "Savings",
        "tile.savings.desc": "Goals · 12% fixed deposits · Chamas",
        "tile.insurance.label": "Insurance",
        "tile.insurance.desc": "Loans · Devices · Health micro",
        "tile.investments.label": "Investments",
        "tile.investments.desc": "T-Bills · Bonds · Money Market",
        "tile.shop.label": "Marketplace",
        "tile.shop.desc": "Shop · Suppliers · Reviews",

        // === Tier perks ===
        "perks.title": "Your Perks",
        "perks.subtitle": "Live benefits from your {tier} tier",
        "perks.loan_discount": "Loan rate discount",
        "perks.mmf_boost": "MMF yield boost",
        "perks.deposit_boost": "Deposit APR boost",
        "perks.none": "Unlock more boosters to reveal interest discounts & yield boosts.",
        "perks.boost_label": "+{n}% boost",
        "perks.discount_label": "−{n}%",

        // === Welcome Tour ===
        "tour.next": "Next",
        "tour.back": "Back",
        "tour.skip": "Skip",
        "tour.done": "Got it",
        "tour.step": "{n} of {total}",
        "tour.s1.title": "Welcome to OnePass, {name}",
        "tour.s1.body": "Your unified credit & money OS. Let's show you what's already working for you.",
        "tour.s2.title": "Your Credit DNA Score",
        "tour.s2.body": "Score {score} ({grade}). It's the engine behind every loan, limit, and perk you unlock.",
        "tour.s3.title": "{tier} tier perks active",
        "tour.s3.body": "You've unlocked {count} of {total} boosters. Your perks (interest discounts, yield boosts) are applied automatically.",
        "tour.s3.body_starter": "Unlock {needed} more booster to climb to Bronze tier and start earning perks.",
        "tour.s4.title": "Next quick win",
        "tour.s4.body": "{title} (+{points} pts). {description}",
        "tour.s4.cta": "Take me there",
        "tour.s4.body_max": "You're already at the top tier — keep your wallet active to maintain your edge.",

        // === SplitPay ===
        "splitpay.title": "SplitPay™",
        "splitpay.bulk_title": "Bulk Credit Order",
        "splitpay.list.subtitle_consumer": "Group co-borrowed loans",
        "splitpay.list.subtitle_business": "Group & bulk-order credit",
        "splitpay.list.new": "Start a new SplitPay",
        "splitpay.list.new_business": "Start a new SplitPay / Bulk order",
        "splitpay.list.section.invites": "Action needed",
        "splitpay.list.section.active": "Active",
        "splitpay.list.section.past": "Past",
        "splitpay.list.empty_title": "No SplitPay loans yet",
        "splitpay.list.empty_consumer": "Borrow as a group, repay your share. Friends can split a purchase or pool funds for a shared goal.",
        "splitpay.list.empty_business": "Borrow with partner businesses or extend wholesale credit to your buyers in just a few taps.",
        "splitpay.step": "Step {n} of 3",
        "splitpay.step1.title_consumer": "How much do you need?",
        "splitpay.step1.title_bulk": "Total bulk order value",
        "splitpay.step1.tenure": "Tenure",
        "splitpay.step1.purpose_consumer": "What's this for? (optional)",
        "splitpay.step1.purpose_bulk": "Order description",
        "splitpay.step1.cta_consumer": "Invite friends",
        "splitpay.step1.cta_bulk": "Invite buyers",
        "splitpay.mode.peer": "Peer SplitPay",
        "splitpay.mode.peer_sub": "Co-borrow with partners",
        "splitpay.mode.supplier": "Bulk Order",
        "splitpay.mode.supplier_sub": "Issue credit to buyers",
        "splitpay.cosigners": "Co-signers",
        "splitpay.cosigners.help": "Each friend covers their own share",
        "splitpay.per_share": "Per-person share",
        "splitpay.fund.title": "Who receives the funds?",
        "splitpay.fund.bulk_title": "Buyers pay you (the supplier)",
        "splitpay.fund.bulk_help": "Buyers take their share on credit; repayments are credited to your wallet automatically.",
        "splitpay.fund.initiator": "To my wallet",
        "splitpay.fund.initiator_sub": "I'll spend it for the group",
        "splitpay.fund.split": "Split equally",
        "splitpay.fund.split_sub": "Each friend gets their share",
        "splitpay.review.each_pays": "Each member pays",
        "splitpay.review.bulk_subtitle": "Bulk order",
        "splitpay.review.peer_subtitle": "Group loan",
        "splitpay.submit": "Send invites",
        "splitpay.invite_banner.title": "You're invited",
        "splitpay.invite_banner.body": "{name} invited you to co-sign this loan. Your share is {amount} due in {days} days.",
        "splitpay.accept": "Accept invite",
        "splitpay.decline": "Decline",
        "splitpay.my_share": "My share",
        "splitpay.repay_btn": "Repay my share ({amount})",
        "splitpay.supplier_position": "Supplier position",
        "splitpay.share_status.pending_acceptance": "Pending acceptance",
        "splitpay.share_status.active": "Active",
        "splitpay.share_status.repaid": "Repaid",
        "splitpay.share_status.overdue": "Overdue",
        "splitpay.share_status.cancelled": "Cancelled",
        "splitpay.toast.sent": "SplitPay invites sent",
        "splitpay.toast.bulk_sent": "Bulk-order invites sent",
        "splitpay.toast.activated": "Loan activated & funds disbursed!",
        "splitpay.toast.waiting": "Accepted. Waiting for other co-signers.",
        "splitpay.toast.declined": "Declined. The loan was cancelled.",
        "splitpay.toast.repaid_partial": "Paid {amount}",
        "splitpay.toast.repaid_all": "Group loan fully paid off!",
        "splitpay.toast.invite_min": "Invite at least one friend",
        "splitpay.group_score.title": "Group Credit DNA",
        "splitpay.group_score.avg": "Member avg",
        "splitpay.group_score.spread": "Tier spread",
        "splitpay.group_score.on_time_bonus": "On-time bonus active",
        "splitpay.group_score.overdue_penalty": "{count} member(s) overdue",
        "splitpay.group_score.spread_penalty": "−{pts} pts: wide tier spread",
        "splitpay.group_score.healthy": "Healthy group · everyone aligned",
    },
    sw: {
        // Swahili (Kenya)
        "common.welcome": "Karibu",
        "common.welcome_back": "Karibu tena",
        "common.hello": "Habari",
        "common.signin": "Ingia",
        "common.signout": "Toka",
        "common.continue": "Endelea",
        "common.cancel": "Ghairi",
        "common.save": "Hifadhi",
        "common.confirm": "Thibitisha",
        "common.loading": "Inapakia…",
        "common.see_all": "Angalia Yote",
        "common.view_all": "Angalia Yote",
        "common.demo_accounts": "Akaunti za demo",
        "common.create_account": "Fungua akaunti",
        "common.processing": "Inashughulikiwa…",
        "common.email": "Barua pepe",
        "common.password": "Nenosiri",
        "common.full_name": "Jina kamili",
        "common.phone": "Simu",
        "common.country": "Nchi",
        "common.currency": "Sarafu",
        "common.business_name": "Jina la biashara",
        "common.individual": "Mtu binafsi",
        "common.business": "Biashara",
        "common.language": "Lugha",
        "common.notifications": "Arifa",
        "common.mark_all_read": "Soma zote",
        "common.no_notifications": "Hakuna arifa mpya",
        "common.activity": "Shughuli",
        "common.profile": "Wasifu",
        "common.home": "Nyumbani",
        "common.credit": "Mkopo",
        "common.wallet": "Pochi",
        "common.loans": "Mikopo",
        "common.more": "Zaidi",
        "common.finance": "Fedha",
        "home.good_to_see_you": "Tunafurahi kukuona tena!",
        "home.credit_dna": "Alama ya Mkopo DNA™",
        "home.your_on_track": "Uko sawa!",
        "home.view_factors": "Angalia sababu za alama",
        "home.onepass_wallet": "Pochi ya OnePass",
        "home.add_money": "Ongeza Pesa",
        "home.send": "Tuma",
        "home.history": "Historia",
        "home.available_credit": "Mkopo Unaopatikana",
        "home.unlock_larger_loans": "Pata mikopo mikubwa",
        "home.better_rates": "Viwango vyema · hadi ₦5M",
        "loan.how_much": "Unahitaji kiasi gani?",
        "loan.select_tenure": "Chagua Muda",
        "loan.repayment": "Malipo",
        "loan.due_in": "Inadaiwa baada ya siku {n}",
        "loan.principal": "Kiasi cha msingi",
        "loan.interest": "Riba",
        "loan.fees": "Ada",
        "loan.confirm_loan": "Thibitisha mkopo wako",
        "loan.confirm_receive": "Thibitisha & Pokea Fedha",
        "loan.improve_score": "Boresha alama yako",
        "loan.fast_disburse": "Utapokea fedha ndani ya sekunde 60",
        "loan.approved": "Hongera!",
        "loan.approved_subtitle": "Mkopo wako umeidhinishwa",
        "loan.disbursed_to_wallet": "Imehamishiwa pochi yako",
        "auth.signin_continue": "Ingia ili uendelee na OnePassCredit",
        "auth.create_account_t": "Fungua akaunti yako",
        "auth.join_in_minute": "Jiunge na OnePassCredit chini ya dakika",
        "auth.new_to_onepass": "Ni mpya OnePass?",
        "auth.have_account": "Tayari una akaunti?",
        "wallet.balance": "Salio",
        "wallet.recent_txns": "Miamala ya Hivi Karibuni",
        "wallet.no_txns": "Hakuna miamala bado",
        "wallet.recipient": "Mpokeaji",
        "wallet.amount": "Kiasi",
        "wallet.add_money_t": "Ongeza Pesa",
        "wallet.send_money_t": "Tuma Pesa",
        "wallet.recipient_placeholder": "Simu au jina la mtumiaji",
        "wallet.invalid_amount": "Weka kiasi sahihi",
        "wallet.added": "Imeongeza {amount}",
        "wallet.sent_to": "Imetumwa {amount} kwa {to}",
        "activity.title": "Shughuli",
        "activity.subtitle": "Pesa zako zinasonga",
        "activity.tab_all": "Yote",
        "activity.tab_credit": "Mkopo",
        "activity.tab_wallet": "Pochi",
        "activity.tab_payments": "Malipo",
        "activity.today": "Leo",
        "activity.yesterday": "Jana",
        "activity.empty": "Hakuna shughuli bado",
        "activity.success": "Imefanikiwa",
        "loan.step_of": "Hatua {n} ya {total}",
        "loan.confirm_title": "Thibitisha mkopo wako",
        "loan.product": "Bidhaa",
        "loan.amount": "Kiasi",
        "loan.tenure": "Muda",
        "loan.total_repayment": "Jumla ya malipo",
        "loan.repay_on_time_title": "Lipa kwa wakati",
        "loan.repay_on_time_sub": "Endelea na malipo kwa ratiba",
        "loan.use_responsibly_title": "Tumia mkopo kwa uangalifu",
        "loan.use_responsibly_sub": "Kopa kile tu unachohitaji",
        "loan.grow_activity_title": "Kuza shughuli za pochi",
        "loan.grow_activity_sub": "Shughuli zaidi, ofa bora",
        "loan.disbursed_to_wallet": "Imehamishiwa pochi yako",
        "loan.repayment_details": "Maelezo ya Malipo",
        "loan.due_date": "Tarehe ya kulipa",
        "loan.approving": "Inaidhinishwa...",
        "loan.done": "Imekamilika",
        // LMS — schedule + statement + actions
        "loan.details": "Maelezo ya Mkopo",
        "loan.outstanding": "Bado kulipwa",
        "loan.repaid": "imelipwa",
        "loan.total": "jumla",
        "loan.late_fees": "Ada za kuchelewa",
        "loan.tenure_days": "muda wa siku {n}",
        "loan.disbursed_on": "Imehamishwa",
        "loan.due_on": "Inadaiwa",
        "loan.schedule": "Ratiba",
        "loan.statement": "Taarifa",
        "loan.repay_full": "Lipa {amount}",
        "loan.early_settle": "Lipa mapema & okoa",
        "loan.pay_installment": "Lipa {amount}",
        "loan.installment_n_of": "Awamu {n} ya {total}",
        "loan.installment_n": "Awamu {n}",
        "loan.installment_due": "Inadaiwa {date}",
        "loan.status_paid": "Imelipwa",
        "loan.status_upcoming": "Inayofuata",
        "loan.status_partial": "Sehemu",
        "loan.status_overdue": "Imechelewa",
        "loan.no_events": "Hakuna shughuli bado",
        "loan.early.title": "Malipo ya mapema",
        "loan.early.desc": "Lipa mkopo wako sasa na uhifadhi 50% ya riba iliyobaki.",
        "loan.early.accrued": "Riba iliyokusanywa",
        "loan.early.unaccrued": "Riba ambayo haijakusanywa",
        "loan.early.discount": "Punguzo la malipo ya mapema (50%)",
        "loan.early.settlement": "Malipo ya mwisho",
        "loan.early.confirm": "Lipa sasa & okoa {amount}",
        "loan.early.processing": "Inashughulikiwa…",
        "loan.early.toast_saved": "Imelipwa. Umeokoa {amount}!",
        "loan.event.disbursed": "Imepelekwa pochi",
        "loan.event.repaid": "Mkopo umelipwa kikamilifu",
        "loan.event.partial_repaid": "Malipo ya sehemu",
        "loan.event.auto_debited": "Kutolewa moja kwa moja kutoka pochi",
        "loan.event.late_fee": "Ada ya kuchelewa imetozwa",
        "loan.event.overdue": "Mkopo umewekwa kama umechelewa",
        "loan.event.early_repaid": "Malipo ya mapema",
        "loan.event.restructured": "Imerekebishwa na msimamizi",
        "loan.event.contacted": "Umewasilianwa na timu ya ukusanyaji",

        // === Awamu 4: Akiba (Savings) ===
        "savings.title": "Akiba ya OnePass",
        "savings.kpi.total_saved": "Akiba jumla",
        "savings.kpi.projected_interest": "Riba inayotarajiwa",
        "savings.kpi.buckets": "Mafungu",
        "savings.tab.goals": "Malengo",
        "savings.tab.deposits": "Amana",
        "savings.tab.chamas": "Chama",
        "savings.goal.new": "Lengo jipya la akiba",
        "savings.goal.empty": "Weka lengo la ada za shule, kodi, au safari kubwa — uangalie pesa zako zikiongezeka.",
        "savings.goal.label": "Akiba ya Malengo",
        "savings.goal.add": "Ongeza",
        "savings.goal.reached": "Lengo limefikiwa",
        "savings.goal.withdraw_all": "Toa yote kwenda pochi",
        "savings.goal.auto_route": "Hupeleka kiotomatiki {pct}% ya kila ongezeko",
        "savings.dep.new": "Funga amana & pata riba",
        "savings.dep.empty": "Funga pesa zako za pochi na upate hadi 12% APR. Vunja mapema wakati wowote kwa ada ndogo.",
        "savings.dep.label": "Amana za Kudumu",
        "savings.dep.principal": "Mtaji",
        "savings.dep.interest": "Riba",
        "savings.dep.at_maturity": "Mwisho wa muda",
        "savings.dep.locked_until": "Imefungwa hadi {date}",
        "savings.dep.break_early": "Vunja mapema",
        "savings.chama.new": "Anzisha Chama",
        "savings.chama.label": "Vikundi vya Chama",
        "savings.chama.empty": "Akiba ya kikundi inayopokezana — kila mtu anachangia kila mwezi, mwanachama mmoja anapokea fedha kila mzunguko.",
        "savings.chama.pool_now": "Mfuko sasa",
        "savings.chama.my_contribution": "Mchango wangu",
        "savings.chama.target": "Lengo / mzunguko",
        "savings.chama.contribute": "Changia {amount}",
        "savings.chama.join": "Jiunge na chama",
        "savings.chama.open_to_join": "Chama vinavyopatikana kujiunga",
        "savings.dlg.goal.title": "Lengo jipya la akiba",
        "savings.dlg.goal.desc": "Weka lengo, kwa hiari peleka % ya kila ongezeko kiotomatiki.",
        "savings.dlg.goal.template": "Aina",
        "savings.dlg.goal.name": "Jina la lengo",
        "savings.dlg.goal.target": "Kiasi cha lengo",
        "savings.dlg.goal.auto": "Peleka kiotomatiki % ya kila ongezeko (0–100, hiari)",
        "savings.dlg.goal.submit": "Tengeneza lengo",
        "savings.dlg.dep.title": "Funga amana",
        "savings.dlg.dep.desc": "Pesa zako za pochi zinapata riba kulingana na muda. Vunja mapema wakati wowote kwa ada ndogo.",
        "savings.dlg.dep.term": "Chagua muda",
        "savings.dlg.dep.amount": "Kiasi cha kufunga",
        "savings.dlg.dep.projected": "Riba inayotarajiwa",
        "savings.dlg.dep.payout": "Malipo mwisho wa muda",
        "savings.dlg.dep.submit": "Funga amana",
        "savings.dlg.chama.title": "Anzisha Chama",
        "savings.dlg.chama.desc": "Akiba ya kikundi inayopokezana. Kila mzunguko, kila mtu anachangia; mwanachama mmoja anapokea pesa zote.",
        "savings.dlg.chama.name": "Jina la chama",
        "savings.dlg.chama.contribution": "Mchango / mzunguko",
        "savings.dlg.chama.freq": "Marudio",
        "savings.dlg.chama.weekly": "Wiki",
        "savings.dlg.chama.monthly": "Mwezi",
        "savings.dlg.chama.desc_field": "Maelezo (hiari)",
        "savings.dlg.chama.submit": "Tengeneza chama",

        // === Awamu 4: Bima (Insurance) ===
        "insurance.title": "Bima ya OnePass",
        "insurance.subtitle": "Linda mikopo, vifaa, na afya yako — kifuniko kimeunganishwa moja kwa moja na pochi yako.",
        "insurance.tab.policies": "Sera",
        "insurance.tab.shop": "Duka",
        "insurance.tab.claims": "Madai",
        "insurance.empty.policies": "Hakuna sera amilifu — gusa Duka kununua kifuniko.",
        "insurance.empty.claims": "Hakuna madai bado. Gusa sera → Wasilisha dai.",
        "insurance.get_cover": "Pata kifuniko",
        "insurance.file_claim": "Wasilisha dai",
        "insurance.cancel_policy": "Ghairi",
        "insurance.policy.premium": "Ada",
        "insurance.policy.monthly": "Kila mwezi",
        "insurance.policy.coverage": "Kifuniko",
        "insurance.policy.expires": "Kinaisha",
        "insurance.claim.under_review": "Inakaguliwa",
        "insurance.claim.approved": "Imeidhinishwa",
        "insurance.claim.paid": "Imelipwa",
        "insurance.claim.declined": "Imekataliwa",
        "insurance.product.loan_protection": "Ulinzi wa Mkopo",
        "insurance.product.device_protection": "Ulinzi wa Kifaa",
        "insurance.product.health_micro": "Bima ndogo ya Afya",
        "insurance.dlg.loan.title": "Ulinzi wa Mkopo",
        "insurance.dlg.loan.desc": "Ada ya 0.8% ya wakati mmoja inalinda 100% ya mtaji wa mkopo wako endapo kifo au ulemavu wa kudumu.",
        "insurance.dlg.loan.active_loans": "Mikopo hai",
        "insurance.dlg.loan.choose": "Chagua mkopo wa kulinda",
        "insurance.dlg.loan.submit": "Linda mkopo huu",
        "insurance.dlg.device.title": "Ulinzi wa Kifaa",
        "insurance.dlg.device.desc": "Linda simu, laptop & tablet dhidi ya wizi, uharibifu wa skrini, maji, na kuanguka kwa bahati mbaya.",
        "insurance.dlg.device.brand": "Chapa",
        "insurance.dlg.device.model": "Mfano",
        "insurance.dlg.device.value": "Thamani ya kifaa",
        "insurance.dlg.device.submit": "Linda kifaa",
        "insurance.dlg.health.title": "Bima ndogo ya Afya",
        "insurance.dlg.health.desc": "Ushauri wa wagonjwa wa nje, vipimo vya maabara, dawa za maagizo, kikomo cha kulazwa hospitali.",
        "insurance.dlg.health.beneficiaries": "Wanufaika (tenganisha kwa koma, hiari)",
        "insurance.dlg.health.submit": "Washa kifuniko",
        "insurance.dlg.claim.title": "Wasilisha dai",
        "insurance.dlg.claim.desc": "Tuambie kilichotokea na kiasi unachodai. Tutakagua ndani ya saa 48.",
        "insurance.dlg.claim.reason": "Kilichotokea ni nini?",
        "insurance.dlg.claim.amount": "Kiasi kinachodaiwa",
        "insurance.dlg.claim.submit": "Wasilisha dai",

        // === Awamu 4: Uwekezaji (Investments) ===
        "inv.title": "Uwekezaji wa OnePass",
        "inv.kpi.portfolio": "Mali",
        "inv.kpi.projected": "Inayotarajiwa",
        "inv.kpi.holdings": "Umiliki",
        "inv.tab.tbills": "Hati za Hazina",
        "inv.tab.bonds": "Hatifungani",
        "inv.tab.mmfs": "MMF",
        "inv.tbill.buy": "Nunua Hati ya Hazina",
        "inv.tbill.empty": "Nunua Hati za Hazina kwa punguzo na ulipwe thamani kamili zinapokomaa.",
        "inv.bond.buy": "Nunua Hatifungani",
        "inv.bond.empty": "Hatifungani za serikali & miundombinu na malipo ya nusu-mwaka hadi 16.5% kwa mwaka.",
        "inv.mmf.invest": "Wekeza kwenye MMF",
        "inv.mmf.empty": "Money Market Funds — riba ya kila siku, toa wakati wowote (T+1).",
        "inv.col.paid": "Imelipwa",
        "inv.col.discount": "Punguzo",
        "inv.col.at_maturity": "Mwisho wa muda",
        "inv.col.principal": "Mtaji",
        "inv.col.total_coupons": "Jumla ya makato",
        "inv.col.interest": "Riba",
        "inv.col.now": "Sasa",
        "inv.mmf.redeem": "Toa",
        "inv.mmf.full": "Yote",
        "inv.mmf.partial": "Sehemu",
        "inv.dlg.tbill.title": "Nunua Hati ya Hazina",
        "inv.dlg.tbill.desc": "Hati za Hazina ni vyombo vya muda mfupi vya serikali vinavyouzwa kwa punguzo na kurudishwa kwa thamani kamili.",
        "inv.dlg.tbill.face": "Thamani kamili (utapokea mwisho)",
        "inv.dlg.tbill.pay_today": "Unalipa leo",
        "inv.dlg.tbill.discount_earned": "Punguzo lililopatikana",
        "inv.dlg.tbill.receive": "Pokea mwisho wa muda",
        "inv.dlg.bond.title": "Nunua Hatifungani",
        "inv.dlg.bond.desc": "Hatifungani zinalipa riba ya nusu-mwaka na kurudisha mtaji wako mwisho.",
        "inv.dlg.bond.amount": "Kiasi cha kuwekeza",
        "inv.dlg.bond.coupons": "Makato (jumla ya miaka {n})",
        "inv.dlg.bond.final": "Malipo ya mwisho",
        "inv.dlg.mmf.title": "Wekeza kwenye MMF",
        "inv.dlg.mmf.desc": "Money Market Funds — riba ya kila siku, toa wakati wowote (T+1).",
        "inv.dlg.mmf.amount": "Kiasi",
        "inv.dlg.mmf.submit": "Wekeza sasa",

        // === Awamu 4: Biashara (Commerce) ===
        "shop.title": "Biashara ya OnePass",
        "shop.subtitle": "Nunua kutoka kwa wafanyabiashara walioidhinishwa · {merchants} maduka · {suppliers} wasambazaji",
        "shop.tab.shop": "Duka",
        "shop.tab.merchants": "Maduka",
        "shop.tab.suppliers": "Wasambazaji",
        "shop.tab.orders": "Maagizo",
        "shop.search": "Tafuta bidhaa",
        "shop.empty.search": "Hakuna bidhaa zilizopatikana. Jaribu utafutaji tofauti.",
        "shop.empty.merchants": "Hakuna wafanyabiashara amilifu bado.",
        "shop.empty.suppliers": "Hakuna wasambazaji katika orodha bado.",
        "shop.empty.orders": "Hakuna maagizo bado. Anza kununua!",
        "shop.add": "Ongeza",
        "shop.left": "imebaki",
        "shop.checkout": "Lipa · vitu {items} · {total}",
        "shop.order.pending_fulfilment": "Inasubiri utimizaji",
        "shop.order.fulfilled": "Imetimizwa",
        "shop.order.cancelled": "Imeghairiwa",
        "shop.order.cancel": "Ghairi agizo",
        "shop.review.write": "Kagua {name}",
        "shop.review.title": "Kagua {name}",
        "shop.review.desc": "Uzoefu wako ulikuwaje? Ukadiriaji wako unawasaidia wanunuzi wengine.",
        "shop.review.comment": "Hiari — shiriki ulichopenda au usichopenda",
        "shop.review.submit": "Chapisha ukaguzi",
        "shop.merchant.products": "bidhaa {n}",

        // === Awamu 4: KYC ===
        "kyc.title": "Uthibitishaji wa Utambulisho",
        "kyc.subtitle": "Thibitisha utambulisho wako ili kufungua mipaka ya juu ya mkopo & ongezeko la alama +80.",
        "kyc.status.title": "Hali",
        "kyc.status.pending": "Haijaanza",
        "kyc.status.submitted": "Inakaguliwa",
        "kyc.status.verified": "Imethibitishwa",
        "kyc.status.declined": "Imekataliwa",
        "kyc.reviewer_note": "Maoni ya mkaguzi:",
        "kyc.verified.title": "Umethibitishwa!",
        "kyc.verified.desc": "Mipaka ya juu ya mkopo & viwango bora vimefunguliwa. Alama imeongezwa +80.",
        "kyc.submitted.title": "Imewasilishwa",
        "kyc.submitted.desc": "Hati zako zinakaguliwa. Tutakujulisha ndani ya saa 24.",
        "kyc.form.id_type": "Aina ya Kitambulisho",
        "kyc.form.id_number": "Nambari ya Kitambulisho",
        "kyc.form.id_placeholder": "mfano 12345678901",
        "kyc.form.dob": "Tarehe ya kuzaliwa",
        "kyc.form.address": "Anwani",
        "kyc.form.address_ph": "Mji, Mkoa",
        "kyc.upload.selfie": "Picha ya uso",
        "kyc.upload.selfie_hint": "Picha wazi ya uso wako",
        "kyc.upload.id_front": "Mbele ya kitambulisho",
        "kyc.upload.id_front_hint": "Upande wa mbele wa kitambulisho chako",
        "kyc.upload.id_back": "Nyuma ya kitambulisho (hiari)",
        "kyc.upload.id_back_hint": "Upande wa nyuma, kama unahitajika",
        "kyc.upload.replace": "Badilisha",
        "kyc.upload.upload": "Pakia",
        "kyc.submit": "Wasilisha kwa uthibitishaji",
        "kyc.submitting": "Inawasilishwa…",

        "kyc.cta.verified": "Utambulisho umethibitishwa",
        "kyc.cta.submitted": "Inakaguliwa",
        "kyc.cta.pending": "Thibitisha utambulisho wako",
        "kyc.cta.verified_sub": "Mipaka ya juu & alama +80 zimefunguliwa",
        "kyc.cta.submitted_sub": "Hati zinakaguliwa — SLA ya saa 24",
        "kyc.cta.pending_sub": "Fungua mipaka ya juu & ongezeko la alama +80",

        "boosters.cta_title": "Vichocheo vya Alama",
        "boosters.cta_sub": "Ona kinachoongeza Alama yako ya Mkopo",
        "boosters.title": "Vichocheo vya Alama",
        "boosters.tier_now": "Daraja la sasa",
        "boosters.score_now": "Alama sasa",
        "boosters.unlocked_count": "{n} kati ya {total} vimefunguliwa",
        "boosters.next_tier": "Inayofuata: {tier}",
        "boosters.already_earned": "Umeshapata",
        "boosters.credit_points": "alama za mkopo",
        "boosters.up_for_grabs": "Zinazongoja",
        "boosters.unlock_to_claim": "fungua kudai",
        "boosters.projected": "Fungua kila kichocheo ili ufikie alama inayotarajiwa ya {score}.",
        "boosters.status_unlocked": "Imefunguliwa",

        "tile.savings.label": "Akiba",
        "tile.savings.desc": "Malengo · Amana 12% · Chama",
        "tile.insurance.label": "Bima",
        "tile.insurance.desc": "Mikopo · Vifaa · Afya ndogo",
        "tile.investments.label": "Uwekezaji",
        "tile.investments.desc": "Hati za Hazina · Hatifungani · MMF",
        "tile.shop.label": "Biashara",
        "tile.shop.desc": "Nunua · Wasambazaji · Ukaguzi",

        // === Faida za Daraja (Tier perks) ===
        "perks.title": "Faida Zako",
        "perks.subtitle": "Faida za moja kwa moja kutoka daraja la {tier}",
        "perks.loan_discount": "Punguzo la riba ya mkopo",
        "perks.mmf_boost": "Ongezeko la riba ya MMF",
        "perks.deposit_boost": "Ongezeko la APR ya amana",
        "perks.none": "Fungua vichocheo zaidi ili kupata punguzo la riba & ongezeko la mavuno.",
        "perks.boost_label": "+{n}% ongezeko",
        "perks.discount_label": "−{n}%",

        // === Ziara ya Karibu (Welcome Tour) ===
        "tour.next": "Endelea",
        "tour.back": "Rudi",
        "tour.skip": "Ruka",
        "tour.done": "Nimepokea",
        "tour.step": "{n} ya {total}",
        "tour.s1.title": "Karibu OnePass, {name}",
        "tour.s1.body": "Mfumo wako wa pamoja wa mkopo na pesa. Tukuonyeshe kilichoshafanya kazi kwako.",
        "tour.s2.title": "Alama yako ya Mkopo DNA",
        "tour.s2.body": "Alama {score} ({grade}). Hii ndio injini nyuma ya kila mkopo, kikomo, na faida unayofungua.",
        "tour.s3.title": "Faida za daraja la {tier} zinatumika",
        "tour.s3.body": "Umefungua vichocheo {count} kati ya {total}. Faida zako (punguzo la riba, ongezeko la mavuno) zinaongezwa moja kwa moja.",
        "tour.s3.body_starter": "Fungua kichocheo {needed} zaidi kupanda hadi daraja la Bronze na uanze kupata faida.",
        "tour.s4.title": "Ushindi wa haraka unaofuata",
        "tour.s4.body": "{title} (+{points} alama). {description}",
        "tour.s4.cta": "Nipeleke huko",
        "tour.s4.body_max": "Tayari upo daraja la juu — endelea kutumia pochi yako ili kuthibitisha hali yako.",

        // === SplitPay (Kiswahili) ===
        "splitpay.title": "SplitPay™",
        "splitpay.bulk_title": "Agizo la Mkopo wa Pamoja",
        "splitpay.list.subtitle_consumer": "Mikopo ya kikundi",
        "splitpay.list.subtitle_business": "Mikopo ya kikundi & maagizo ya wengi",
        "splitpay.list.new": "Anza SplitPay mpya",
        "splitpay.list.new_business": "Anza SplitPay / Agizo Jipya",
        "splitpay.list.section.invites": "Hatua inahitajika",
        "splitpay.list.section.active": "Inaendelea",
        "splitpay.list.section.past": "Imepita",
        "splitpay.list.empty_title": "Hakuna SplitPay bado",
        "splitpay.list.empty_consumer": "Kopa kama kikundi, lipa sehemu yako. Marafiki wanaweza kushiriki ununuzi au kuchanga fedha kwa lengo la pamoja.",
        "splitpay.list.empty_business": "Kopa na biashara nyingine au toa mkopo wa jumla kwa wateja wako kwa muda mfupi.",
        "splitpay.step": "Hatua {n} ya 3",
        "splitpay.step1.title_consumer": "Unahitaji kiasi gani?",
        "splitpay.step1.title_bulk": "Thamani ya agizo la jumla",
        "splitpay.step1.tenure": "Muda",
        "splitpay.step1.purpose_consumer": "Hii ni ya nini? (hiari)",
        "splitpay.step1.purpose_bulk": "Maelezo ya agizo",
        "splitpay.step1.cta_consumer": "Alika marafiki",
        "splitpay.step1.cta_bulk": "Alika wanunuzi",
        "splitpay.mode.peer": "SplitPay ya Pamoja",
        "splitpay.mode.peer_sub": "Kopa pamoja na washirika",
        "splitpay.mode.supplier": "Agizo la Jumla",
        "splitpay.mode.supplier_sub": "Toa mkopo kwa wanunuzi",
        "splitpay.cosigners": "Wasimamizi-wenza",
        "splitpay.cosigners.help": "Kila rafiki analipa sehemu yake",
        "splitpay.per_share": "Sehemu ya kila mtu",
        "splitpay.fund.title": "Nani anapokea fedha?",
        "splitpay.fund.bulk_title": "Wanunuzi watakulipa (wewe muuzaji)",
        "splitpay.fund.bulk_help": "Wanunuzi wanachukua sehemu yao kwa mkopo; malipo yatawekwa kwenye pochi yako moja kwa moja.",
        "splitpay.fund.initiator": "Kwa pochi yangu",
        "splitpay.fund.initiator_sub": "Nitatumia kwa ajili ya kikundi",
        "splitpay.fund.split": "Gawanya sawa",
        "splitpay.fund.split_sub": "Kila rafiki anapata sehemu yake",
        "splitpay.review.each_pays": "Kila mwanachama analipa",
        "splitpay.review.bulk_subtitle": "Agizo la jumla",
        "splitpay.review.peer_subtitle": "Mkopo wa kikundi",
        "splitpay.submit": "Tuma mialiko",
        "splitpay.invite_banner.title": "Umealikwa",
        "splitpay.invite_banner.body": "{name} amekualika kusimamia mkopo huu. Sehemu yako ni {amount}, inaisha siku {days}.",
        "splitpay.accept": "Kubali mwaliko",
        "splitpay.decline": "Kataa",
        "splitpay.my_share": "Sehemu yangu",
        "splitpay.repay_btn": "Lipa sehemu yangu ({amount})",
        "splitpay.supplier_position": "Hali ya muuzaji",
        "splitpay.share_status.pending_acceptance": "Inasubiri kibali",
        "splitpay.share_status.active": "Inaendelea",
        "splitpay.share_status.repaid": "Imelipwa",
        "splitpay.share_status.overdue": "Imechelewa",
        "splitpay.share_status.cancelled": "Imeghairiwa",
        "splitpay.toast.sent": "Mialiko ya SplitPay imetumwa",
        "splitpay.toast.bulk_sent": "Mialiko ya agizo imetumwa",
        "splitpay.toast.activated": "Mkopo umeanza & fedha zimetumwa!",
        "splitpay.toast.waiting": "Imekubaliwa. Inasubiri wengine.",
        "splitpay.toast.declined": "Imekataliwa. Mkopo umeghairiwa.",
        "splitpay.toast.repaid_partial": "Umelipa {amount}",
        "splitpay.toast.repaid_all": "Mkopo wa kikundi umelipwa kabisa!",
        "splitpay.toast.invite_min": "Alika angalau rafiki mmoja",
        "splitpay.group_score.title": "DNA ya Mkopo wa Kikundi",
        "splitpay.group_score.avg": "Wastani wa wanachama",
        "splitpay.group_score.spread": "Tofauti ya daraja",
        "splitpay.group_score.on_time_bonus": "Bonasi ya kulipa kwa wakati",
        "splitpay.group_score.overdue_penalty": "Wanachama {count} wamechelewa",
        "splitpay.group_score.spread_penalty": "−{pts} alama: tofauti kubwa ya daraja",
        "splitpay.group_score.healthy": "Kikundi chenye afya · kila mtu yupo sawa",
    },
    fr: {
        // French (for francophone West Africa - optional)
        "common.welcome": "Bienvenue",
        "common.welcome_back": "Bon retour",
        "common.hello": "Bonjour",
        "common.signin": "Connexion",
        "common.signout": "Déconnexion",
        "common.continue": "Continuer",
        "common.cancel": "Annuler",
        "common.save": "Enregistrer",
        "common.confirm": "Confirmer",
        "common.loading": "Chargement…",
        "common.see_all": "Voir tout",
        "common.view_all": "Voir tout",
        "common.demo_accounts": "Comptes démo",
        "common.create_account": "Créer un compte",
        "common.processing": "Traitement…",
        "common.email": "E-mail",
        "common.password": "Mot de passe",
        "common.full_name": "Nom complet",
        "common.phone": "Téléphone",
        "common.country": "Pays",
        "common.currency": "Devise",
        "common.business_name": "Nom de l'entreprise",
        "common.individual": "Particulier",
        "common.business": "Entreprise",
        "common.language": "Langue",
        "common.notifications": "Notifications",
        "common.mark_all_read": "Tout marquer lu",
        "common.no_notifications": "Vous êtes à jour",
        "common.activity": "Activité",
        "common.profile": "Profil",
        "common.home": "Accueil",
        "common.credit": "Crédit",
        "common.wallet": "Portefeuille",
        "common.loans": "Prêts",
        "common.more": "Plus",
        "common.finance": "Finance",
        "home.good_to_see_you": "Heureux de vous revoir !",
        "home.credit_dna": "Score Credit DNA™",
        "home.your_on_track": "Tout va bien !",
        "home.view_factors": "Voir les facteurs",
        "home.onepass_wallet": "Portefeuille OnePass",
        "home.add_money": "Ajouter",
        "home.send": "Envoyer",
        "home.history": "Historique",
        "home.available_credit": "Crédit disponible",
        "home.unlock_larger_loans": "Prêts plus importants",
        "home.better_rates": "Meilleurs taux · jusqu'à ₦5M",
        "loan.how_much": "De combien avez-vous besoin ?",
        "loan.select_tenure": "Choisir la durée",
        "loan.repayment": "Remboursement",
        "loan.due_in": "Dû dans {n} jours",
        "loan.principal": "Principal",
        "loan.interest": "Intérêts",
        "loan.fees": "Frais",
        "loan.confirm_loan": "Confirmer votre prêt",
        "loan.confirm_receive": "Confirmer et recevoir",
        "loan.improve_score": "Améliorez votre score",
        "loan.fast_disburse": "Fonds reçus en moins de 60 secondes",
        "loan.approved": "Félicitations !",
        "loan.approved_subtitle": "Votre prêt est approuvé",
        "loan.disbursed_to_wallet": "Versé sur votre portefeuille",
        "auth.signin_continue": "Connectez-vous pour continuer",
        "auth.create_account_t": "Créez votre compte",
        "auth.join_in_minute": "Rejoignez OnePassCredit en moins d'une minute",
        "auth.new_to_onepass": "Nouveau sur OnePass ?",
        "auth.have_account": "Vous avez déjà un compte ?",
        "wallet.balance": "Solde",
        "wallet.recent_txns": "Transactions récentes",
        "wallet.no_txns": "Aucune transaction",
        "wallet.recipient": "Destinataire",
        "wallet.amount": "Montant",
        "wallet.add_money_t": "Ajouter de l'argent",
        "wallet.send_money_t": "Envoyer de l'argent",
        "wallet.recipient_placeholder": "Téléphone ou nom d'utilisateur",
        "wallet.invalid_amount": "Saisissez un montant valide",
        "wallet.added": "{amount} ajouté",
        "wallet.sent_to": "{amount} envoyé à {to}",
        "activity.title": "Activité",
        "activity.subtitle": "Votre argent en mouvement",
        "activity.tab_all": "Tout",
        "activity.tab_credit": "Crédit",
        "activity.tab_wallet": "Portefeuille",
        "activity.tab_payments": "Paiements",
        "activity.today": "Aujourd'hui",
        "activity.yesterday": "Hier",
        "activity.empty": "Pas encore d'activité",
        "activity.success": "Succès",
        "loan.step_of": "Étape {n} sur {total}",
        "loan.confirm_title": "Confirmez votre prêt",
        "loan.product": "Produit",
        "loan.amount": "Montant",
        "loan.tenure": "Durée",
        "loan.total_repayment": "Remboursement total",
        "loan.repay_on_time_title": "Remboursez à temps",
        "loan.repay_on_time_sub": "Tenez vos paiements à jour",
        "loan.use_responsibly_title": "Utilisez le crédit avec sagesse",
        "loan.use_responsibly_sub": "N'empruntez que ce dont vous avez besoin",
        "loan.grow_activity_title": "Augmentez votre activité",
        "loan.grow_activity_sub": "Plus d'activité, meilleures offres",
        "loan.disbursed_to_wallet": "Versé sur votre portefeuille",
        "loan.repayment_details": "Détails de remboursement",
        "loan.due_date": "Échéance",
        "loan.approving": "Approbation...",
        "loan.done": "Terminé",
        // LMS — schedule + statement + actions
        "loan.details": "Détails du prêt",
        "loan.outstanding": "Restant dû",
        "loan.repaid": "remboursé",
        "loan.total": "total",
        "loan.late_fees": "Frais de retard",
        "loan.tenure_days": "Durée de {n}j",
        "loan.disbursed_on": "Versé",
        "loan.due_on": "Échéance",
        "loan.schedule": "Calendrier",
        "loan.statement": "Relevé",
        "loan.repay_full": "Rembourser {amount}",
        "loan.early_settle": "Soldé à l'avance",
        "loan.pay_installment": "Payer {amount}",
        "loan.installment_n_of": "Mensualité {n} sur {total}",
        "loan.installment_n": "Mensualité {n}",
        "loan.installment_due": "Échéance {date}",
        "loan.status_paid": "Payé",
        "loan.status_upcoming": "À venir",
        "loan.status_partial": "Partiel",
        "loan.status_overdue": "En retard",
        "loan.no_events": "Aucun événement",
        "loan.early.title": "Règlement anticipé",
        "loan.early.desc": "Soldez votre prêt maintenant et gardez 50% des intérêts non échus.",
        "loan.early.accrued": "Intérêts échus",
        "loan.early.unaccrued": "Intérêts non échus",
        "loan.early.discount": "Remise (50%)",
        "loan.early.settlement": "Règlement",
        "loan.early.confirm": "Solder & économiser {amount}",
        "loan.early.processing": "Traitement…",
        "loan.early.toast_saved": "Soldé. Vous avez économisé {amount}!",
        "loan.event.disbursed": "Versé au portefeuille",
        "loan.event.repaid": "Prêt entièrement remboursé",
        "loan.event.partial_repaid": "Remboursement partiel",
        "loan.event.auto_debited": "Prélèvement automatique",
        "loan.event.late_fee": "Frais de retard appliqués",
        "loan.event.overdue": "Prêt marqué en retard",
        "loan.event.early_repaid": "Règlement anticipé",
        "loan.event.restructured": "Restructuré par l'administrateur",
        "loan.event.contacted": "Contacté par le recouvrement",

        // === Phase 4: Épargne (Savings) ===
        "savings.title": "Épargne OnePass",
        "savings.kpi.total_saved": "Total épargné",
        "savings.kpi.projected_interest": "Intérêts projetés",
        "savings.kpi.buckets": "Comptes",
        "savings.tab.goals": "Objectifs",
        "savings.tab.deposits": "Dépôts",
        "savings.tab.chamas": "Tontines",
        "savings.goal.new": "Nouvel objectif d'épargne",
        "savings.goal.empty": "Fixez un objectif pour les frais de scolarité, le loyer ou un grand voyage — et regardez votre solde grandir.",
        "savings.goal.label": "Objectifs d'Épargne",
        "savings.goal.add": "Ajouter",
        "savings.goal.reached": "Atteint",
        "savings.goal.withdraw_all": "Tout retirer vers le portefeuille",
        "savings.goal.auto_route": "Achemine automatiquement {pct}% de chaque recharge",
        "savings.dep.new": "Verrouiller un dépôt & gagner",
        "savings.dep.empty": "Verrouillez le solde inactif et gagnez jusqu'à 12% APR. Cassez tôt à tout moment pour des frais minimes.",
        "savings.dep.label": "Dépôts à Terme",
        "savings.dep.principal": "Principal",
        "savings.dep.interest": "Intérêts",
        "savings.dep.at_maturity": "À l'échéance",
        "savings.dep.locked_until": "Verrouillé jusqu'au {date}",
        "savings.dep.break_early": "Casser tôt",
        "savings.chama.new": "Démarrer une Tontine",
        "savings.chama.label": "Groupes Tontine",
        "savings.chama.empty": "Épargne rotative en groupe — chacun cotise chaque mois, un membre reçoit la cagnotte à chaque cycle.",
        "savings.chama.pool_now": "Cagnotte",
        "savings.chama.my_contribution": "Ma contribution",
        "savings.chama.target": "Objectif / cycle",
        "savings.chama.contribute": "Cotiser {amount}",
        "savings.chama.join": "Rejoindre la tontine",
        "savings.chama.open_to_join": "Tontines ouvertes à rejoindre",
        "savings.dlg.goal.title": "Nouvel objectif d'épargne",
        "savings.dlg.goal.desc": "Fixez un objectif, acheminez éventuellement un % de chaque recharge automatiquement.",
        "savings.dlg.goal.template": "Modèle",
        "savings.dlg.goal.name": "Nom de l'objectif",
        "savings.dlg.goal.target": "Montant cible",
        "savings.dlg.goal.auto": "Acheminer % de chaque recharge automatiquement (0–100, optionnel)",
        "savings.dlg.goal.submit": "Créer l'objectif",
        "savings.dlg.dep.title": "Verrouiller un dépôt à terme",
        "savings.dlg.dep.desc": "Les fonds inactifs du portefeuille rapportent un intérêt progressif. Cassez tôt à tout moment pour des frais minimes.",
        "savings.dlg.dep.term": "Choisir la durée",
        "savings.dlg.dep.amount": "Montant à verrouiller",
        "savings.dlg.dep.projected": "Intérêts projetés",
        "savings.dlg.dep.payout": "Versement à l'échéance",
        "savings.dlg.dep.submit": "Verrouiller le dépôt",
        "savings.dlg.chama.title": "Démarrer une Tontine",
        "savings.dlg.chama.desc": "Épargne rotative en groupe. Chaque cycle, tout le monde cotise; un membre reçoit la cagnotte complète.",
        "savings.dlg.chama.name": "Nom de la tontine",
        "savings.dlg.chama.contribution": "Cotisation / cycle",
        "savings.dlg.chama.freq": "Fréquence",
        "savings.dlg.chama.weekly": "Hebdomadaire",
        "savings.dlg.chama.monthly": "Mensuelle",
        "savings.dlg.chama.desc_field": "Description (optionnel)",
        "savings.dlg.chama.submit": "Créer la tontine",

        // === Phase 4: Assurance (Insurance) ===
        "insurance.title": "Assurance OnePass",
        "insurance.subtitle": "Protégez vos prêts, vos appareils et votre santé — couvertures attachées directement à votre portefeuille.",
        "insurance.tab.policies": "Polices",
        "insurance.tab.shop": "Boutique",
        "insurance.tab.claims": "Réclamations",
        "insurance.empty.policies": "Aucune police active — appuyez sur Boutique pour acheter une couverture.",
        "insurance.empty.claims": "Aucune réclamation. Appuyez sur une police → Déposer une réclamation.",
        "insurance.get_cover": "Souscrire",
        "insurance.file_claim": "Déposer une réclamation",
        "insurance.cancel_policy": "Annuler",
        "insurance.policy.premium": "Prime",
        "insurance.policy.monthly": "Mensuelle",
        "insurance.policy.coverage": "Couverture",
        "insurance.policy.expires": "Expire",
        "insurance.claim.under_review": "En cours d'examen",
        "insurance.claim.approved": "Approuvée",
        "insurance.claim.paid": "Versée",
        "insurance.claim.declined": "Refusée",
        "insurance.product.loan_protection": "Protection de Prêt",
        "insurance.product.device_protection": "Protection d'Appareil",
        "insurance.product.health_micro": "Micro-Assurance Santé",
        "insurance.dlg.loan.title": "Protection de Prêt",
        "insurance.dlg.loan.desc": "Prime unique de 0.8% couvrant 100% du principal du prêt en cas de décès ou d'invalidité permanente.",
        "insurance.dlg.loan.active_loans": "Prêts actifs",
        "insurance.dlg.loan.choose": "Choisir le prêt à protéger",
        "insurance.dlg.loan.submit": "Protéger ce prêt",
        "insurance.dlg.device.title": "Protection d'Appareil",
        "insurance.dlg.device.desc": "Couvrez téléphones, ordinateurs portables et tablettes contre le vol, l'écran cassé, les dégâts des eaux et les chutes accidentelles.",
        "insurance.dlg.device.brand": "Marque",
        "insurance.dlg.device.model": "Modèle",
        "insurance.dlg.device.value": "Valeur de l'appareil",
        "insurance.dlg.device.submit": "Protéger l'appareil",
        "insurance.dlg.health.title": "Micro-Assurance Santé",
        "insurance.dlg.health.desc": "Consultations externes, analyses de laboratoire, médicaments sur ordonnance, plafond d'hospitalisation.",
        "insurance.dlg.health.beneficiaries": "Bénéficiaires (séparés par virgule, optionnel)",
        "insurance.dlg.health.submit": "Activer la couverture",
        "insurance.dlg.claim.title": "Déposer une réclamation",
        "insurance.dlg.claim.desc": "Dites-nous ce qui s'est passé et combien vous réclamez. Nous examinons sous 48 heures.",
        "insurance.dlg.claim.reason": "Que s'est-il passé?",
        "insurance.dlg.claim.amount": "Montant réclamé",
        "insurance.dlg.claim.submit": "Soumettre la réclamation",

        // === Phase 4: Investissements ===
        "inv.title": "Investissements OnePass",
        "inv.kpi.portfolio": "Portefeuille",
        "inv.kpi.projected": "Projeté",
        "inv.kpi.holdings": "Avoirs",
        "inv.tab.tbills": "Bons du Trésor",
        "inv.tab.bonds": "Obligations",
        "inv.tab.mmfs": "FMM",
        "inv.tbill.buy": "Acheter Bon",
        "inv.tbill.empty": "Achetez des bons du Trésor à escompte et rachetez à leur valeur nominale à l'échéance.",
        "inv.bond.buy": "Acheter une Obligation",
        "inv.bond.empty": "Obligations gouvernementales & d'infrastructure avec coupons semestriels jusqu'à 16.5% p.a.",
        "inv.mmf.invest": "Investir en FMM",
        "inv.mmf.empty": "Fonds Monétaires — capitalisation quotidienne, retrait à tout moment en T+1.",
        "inv.col.paid": "Payé",
        "inv.col.discount": "Escompte",
        "inv.col.at_maturity": "À l'échéance",
        "inv.col.principal": "Principal",
        "inv.col.total_coupons": "Coupons totaux",
        "inv.col.interest": "Intérêts",
        "inv.col.now": "Maintenant",
        "inv.mmf.redeem": "Racheter",
        "inv.mmf.full": "Total",
        "inv.mmf.partial": "Partiel",
        "inv.dlg.tbill.title": "Acheter Bon du Trésor",
        "inv.dlg.tbill.desc": "Les bons du Trésor sont des instruments gouvernementaux à court terme vendus à escompte et rachetés à leur valeur nominale.",
        "inv.dlg.tbill.face": "Valeur nominale (reçue à l'échéance)",
        "inv.dlg.tbill.pay_today": "Vous payez aujourd'hui",
        "inv.dlg.tbill.discount_earned": "Escompte gagné",
        "inv.dlg.tbill.receive": "Reçu à l'échéance",
        "inv.dlg.bond.title": "Acheter une Obligation",
        "inv.dlg.bond.desc": "Les obligations versent des coupons d'intérêt semestriels et restituent votre principal à l'échéance.",
        "inv.dlg.bond.amount": "Montant à investir",
        "inv.dlg.bond.coupons": "Coupons (total {n}a)",
        "inv.dlg.bond.final": "Versement final",
        "inv.dlg.mmf.title": "Investir en FMM",
        "inv.dlg.mmf.desc": "Fonds du Marché Monétaire — rendement quotidien, retrait à tout moment en T+1.",
        "inv.dlg.mmf.amount": "Montant",
        "inv.dlg.mmf.submit": "Investir maintenant",

        // === Phase 4: Commerce ===
        "shop.title": "Commerce OnePass",
        "shop.subtitle": "Achetez auprès de marchands vérifiés · {merchants} boutiques · {suppliers} fournisseurs",
        "shop.tab.shop": "Boutique",
        "shop.tab.merchants": "Boutiques",
        "shop.tab.suppliers": "Fournisseurs",
        "shop.tab.orders": "Commandes",
        "shop.search": "Rechercher des produits",
        "shop.empty.search": "Aucun produit trouvé. Essayez une autre recherche.",
        "shop.empty.merchants": "Aucun marchand actif pour le moment.",
        "shop.empty.suppliers": "Aucun fournisseur dans l'annuaire.",
        "shop.empty.orders": "Aucune commande. Commencez à acheter!",
        "shop.add": "Ajouter",
        "shop.left": "restant",
        "shop.checkout": "Commander · {items} articles · {total}",
        "shop.order.pending_fulfilment": "En attente d'expédition",
        "shop.order.fulfilled": "Livrée",
        "shop.order.cancelled": "Annulée",
        "shop.order.cancel": "Annuler la commande",
        "shop.review.write": "Évaluer {name}",
        "shop.review.title": "Évaluer {name}",
        "shop.review.desc": "Comment était votre expérience? Votre note aide les autres acheteurs.",
        "shop.review.comment": "Optionnel — partagez ce que vous avez aimé ou non",
        "shop.review.submit": "Publier l'avis",
        "shop.merchant.products": "{n} produits",

        // === Phase 4: KYC ===
        "kyc.title": "Vérification d'Identité",
        "kyc.subtitle": "Vérifiez votre identité pour débloquer des limites de prêt plus élevées & un boost de +80 au score de crédit.",
        "kyc.status.title": "Statut",
        "kyc.status.pending": "Non commencé",
        "kyc.status.submitted": "En cours d'examen",
        "kyc.status.verified": "Vérifié",
        "kyc.status.declined": "Refusé",
        "kyc.reviewer_note": "Note de l'examinateur:",
        "kyc.verified.title": "Vous êtes vérifié!",
        "kyc.verified.desc": "Limites de prêt plus élevées & meilleurs taux débloqués. Score de crédit boosté +80.",
        "kyc.submitted.title": "Soumis",
        "kyc.submitted.desc": "Vos documents sont en cours d'examen. Nous vous notifierons sous 24 heures.",
        "kyc.form.id_type": "Type d'ID",
        "kyc.form.id_number": "Numéro d'ID",
        "kyc.form.id_placeholder": "ex. 12345678901",
        "kyc.form.dob": "Date de naissance",
        "kyc.form.address": "Adresse",
        "kyc.form.address_ph": "Ville, État",
        "kyc.upload.selfie": "Selfie",
        "kyc.upload.selfie_hint": "Une photo claire de votre visage",
        "kyc.upload.id_front": "Recto ID",
        "kyc.upload.id_front_hint": "Recto de votre document d'identité",
        "kyc.upload.id_back": "Verso ID (optionnel)",
        "kyc.upload.id_back_hint": "Verso, si applicable",
        "kyc.upload.replace": "Remplacer",
        "kyc.upload.upload": "Téléverser",
        "kyc.submit": "Soumettre pour vérification",
        "kyc.submitting": "Soumission…",

        "kyc.cta.verified": "Identité vérifiée",
        "kyc.cta.submitted": "En cours d'examen",
        "kyc.cta.pending": "Vérifier votre identité",
        "kyc.cta.verified_sub": "Limites plus élevées & boost +80 débloqués",
        "kyc.cta.submitted_sub": "Documents en cours d'examen — SLA 24h",
        "kyc.cta.pending_sub": "Débloquez des limites de prêt plus élevées & un boost de +80",

        "boosters.cta_title": "Boosters de Score",
        "boosters.cta_sub": "Découvrez ce qui fait grimper votre score Credit DNA",
        "boosters.title": "Boosters de Score",
        "boosters.tier_now": "Palier actuel",
        "boosters.score_now": "Score actuel",
        "boosters.unlocked_count": "{n} sur {total} débloqués",
        "boosters.next_tier": "Suivant: {tier}",
        "boosters.already_earned": "Déjà gagnés",
        "boosters.credit_points": "points de crédit",
        "boosters.up_for_grabs": "À débloquer",
        "boosters.unlock_to_claim": "débloquer pour réclamer",
        "boosters.projected": "Débloquez tous les boosters pour atteindre un score projeté de {score}.",
        "boosters.status_unlocked": "Débloqué",

        "tile.savings.label": "Épargne",
        "tile.savings.desc": "Objectifs · Dépôts 12% · Tontines",
        "tile.insurance.label": "Assurance",
        "tile.insurance.desc": "Prêts · Appareils · Micro-Santé",
        "tile.investments.label": "Investissements",
        "tile.investments.desc": "Bons · Obligations · FMM",
        "tile.shop.label": "Marketplace",
        "tile.shop.desc": "Boutique · Fournisseurs · Avis",

        // === Avantages de palier ===
        "perks.title": "Vos avantages",
        "perks.subtitle": "Bénéfices actifs de votre palier {tier}",
        "perks.loan_discount": "Remise sur taux de prêt",
        "perks.mmf_boost": "Bonus rendement FMM",
        "perks.deposit_boost": "Bonus APR sur dépôt",
        "perks.none": "Débloquez plus de boosters pour révéler des remises de taux & bonus de rendement.",
        "perks.boost_label": "+{n}% bonus",
        "perks.discount_label": "−{n}%",

        // === Visite de bienvenue ===
        "tour.next": "Suivant",
        "tour.back": "Retour",
        "tour.skip": "Passer",
        "tour.done": "Compris",
        "tour.step": "{n} sur {total}",
        "tour.s1.title": "Bienvenue sur OnePass, {name}",
        "tour.s1.body": "Votre OS unifié de crédit & d'argent. Découvrons ce qui fonctionne déjà pour vous.",
        "tour.s2.title": "Votre Score Credit DNA",
        "tour.s2.body": "Score {score} ({grade}). C'est le moteur derrière chaque prêt, limite et avantage débloqué.",
        "tour.s3.title": "Avantages {tier} actifs",
        "tour.s3.body": "Vous avez débloqué {count} boosters sur {total}. Vos avantages (remises de taux, bonus de rendement) sont appliqués automatiquement.",
        "tour.s3.body_starter": "Débloquez {needed} booster de plus pour atteindre le palier Bronze et commencer à gagner des avantages.",
        "tour.s4.title": "Prochaine victoire rapide",
        "tour.s4.body": "{title} (+{points} pts). {description}",
        "tour.s4.cta": "Allons-y",
        "tour.s4.body_max": "Vous êtes déjà au sommet — restez actif sur votre portefeuille pour conserver votre avance.",

        // === SplitPay (Français) ===
        "splitpay.title": "SplitPay™",
        "splitpay.bulk_title": "Commande de crédit groupé",
        "splitpay.list.subtitle_consumer": "Prêts co-empruntés en groupe",
        "splitpay.list.subtitle_business": "Crédit groupé & commandes en gros",
        "splitpay.list.new": "Démarrer un nouveau SplitPay",
        "splitpay.list.new_business": "Démarrer SplitPay / Commande groupée",
        "splitpay.list.section.invites": "Action requise",
        "splitpay.list.section.active": "Actifs",
        "splitpay.list.section.past": "Passés",
        "splitpay.list.empty_title": "Aucun SplitPay pour l'instant",
        "splitpay.list.empty_consumer": "Empruntez en groupe, remboursez votre part. Les amis peuvent partager un achat ou mettre en commun pour un objectif.",
        "splitpay.list.empty_business": "Empruntez avec des partenaires ou accordez du crédit en gros à vos acheteurs en quelques clics.",
        "splitpay.step": "Étape {n} sur 3",
        "splitpay.step1.title_consumer": "Combien vous faut-il ?",
        "splitpay.step1.title_bulk": "Valeur totale de la commande",
        "splitpay.step1.tenure": "Durée",
        "splitpay.step1.purpose_consumer": "À quoi ça sert ? (facultatif)",
        "splitpay.step1.purpose_bulk": "Description de la commande",
        "splitpay.step1.cta_consumer": "Inviter des amis",
        "splitpay.step1.cta_bulk": "Inviter des acheteurs",
        "splitpay.mode.peer": "SplitPay entre pairs",
        "splitpay.mode.peer_sub": "Co-emprunter avec des partenaires",
        "splitpay.mode.supplier": "Commande groupée",
        "splitpay.mode.supplier_sub": "Émettre du crédit aux acheteurs",
        "splitpay.cosigners": "Co-signataires",
        "splitpay.cosigners.help": "Chaque ami couvre sa propre part",
        "splitpay.per_share": "Part par personne",
        "splitpay.fund.title": "Qui reçoit les fonds ?",
        "splitpay.fund.bulk_title": "Les acheteurs vous paient (fournisseur)",
        "splitpay.fund.bulk_help": "Les acheteurs prennent leur part à crédit ; les remboursements sont crédités à votre portefeuille automatiquement.",
        "splitpay.fund.initiator": "Sur mon portefeuille",
        "splitpay.fund.initiator_sub": "Je dépenserai pour le groupe",
        "splitpay.fund.split": "Diviser équitablement",
        "splitpay.fund.split_sub": "Chaque ami reçoit sa part",
        "splitpay.review.each_pays": "Chaque membre paie",
        "splitpay.review.bulk_subtitle": "Commande groupée",
        "splitpay.review.peer_subtitle": "Prêt de groupe",
        "splitpay.submit": "Envoyer les invitations",
        "splitpay.invite_banner.title": "Vous êtes invité(e)",
        "splitpay.invite_banner.body": "{name} vous a invité(e) à co-signer ce prêt. Votre part est {amount}, due dans {days} jours.",
        "splitpay.accept": "Accepter l'invitation",
        "splitpay.decline": "Refuser",
        "splitpay.my_share": "Ma part",
        "splitpay.repay_btn": "Rembourser ma part ({amount})",
        "splitpay.supplier_position": "Position du fournisseur",
        "splitpay.share_status.pending_acceptance": "En attente d'acceptation",
        "splitpay.share_status.active": "Actif",
        "splitpay.share_status.repaid": "Remboursé",
        "splitpay.share_status.overdue": "En retard",
        "splitpay.share_status.cancelled": "Annulé",
        "splitpay.toast.sent": "Invitations SplitPay envoyées",
        "splitpay.toast.bulk_sent": "Invitations de commande envoyées",
        "splitpay.toast.activated": "Prêt activé & fonds décaissés !",
        "splitpay.toast.waiting": "Accepté. En attente des autres co-signataires.",
        "splitpay.toast.declined": "Refusé. Le prêt a été annulé.",
        "splitpay.toast.repaid_partial": "{amount} payés",
        "splitpay.toast.repaid_all": "Prêt de groupe entièrement remboursé !",
        "splitpay.toast.invite_min": "Invitez au moins un ami",
        "splitpay.group_score.title": "ADN Crédit Groupe",
        "splitpay.group_score.avg": "Moyenne membres",
        "splitpay.group_score.spread": "Écart de palier",
        "splitpay.group_score.on_time_bonus": "Bonus ponctualité actif",
        "splitpay.group_score.overdue_penalty": "{count} membre(s) en retard",
        "splitpay.group_score.spread_penalty": "−{pts} pts : large écart de palier",
        "splitpay.group_score.healthy": "Groupe sain · tout le monde aligné",
    },
};

export const LANGUAGES = [
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "sw", label: "Kiswahili", flag: "🇰🇪" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
];

const I18nCtx = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
    const [lang, setLangState] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) || "en";
        } catch {
            return "en";
        }
    });

    const setLang = (l) => {
        setLangState(l);
        try {
            localStorage.setItem(STORAGE_KEY, l);
        } catch {}
    };

    const t = (key, vars) => {
        const pack = STRINGS[lang] || STRINGS.en;
        let str = pack[key] || STRINGS.en[key] || key;
        if (vars) {
            Object.entries(vars).forEach(([k, v]) => {
                str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
            });
        }
        return str;
    };

    return <I18nCtx.Provider value={{ lang, setLang, t }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);
